import useDebounce from "@/Hooks/useDebounce/useDebounce";
import { IAppItem } from "@/Models/ApiModels";
import {
  AsyncSearchableSelect,
  TEntitySelectorProps,
} from "@/shared/ui/select";
import styles from "@/shared/ui/select/ui/select.module.scss";
import { useGetAppsQuery } from "@/Store/APIs/Apps.api";
import ApplicationStoreIcon from "@/UI/icons/ApplicationStoreIcon";
import { throwNewToastError } from "@/Utils/MainUtils";
import { ComboboxItem, Flex } from "@mantine/core";
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useMemo,
  useState,
} from "react";

type TApplicationSelectProps = {
  value: IAppItem | null;
  onChange: (app: IAppItem | null) => void;
  clearable?: boolean;
} & TEntitySelectorProps;

export const ApplicationSelector = forwardRef(
  (props: TApplicationSelectProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      value,
      onChange,
      width,
      clearable,
      disabled,
      error,
      iconVisible,
      placeholder,
    } = props;

    const [searchValue, setSearchValue] = useState("");
    const debouncedSearch = useDebounce(searchValue, 500);

    const { data: appsList = [], isFetching } = useGetAppsQuery({
      limit: 999,
      offset: 0,
      search: debouncedSearch,
    });

    const memoizedAppsList = useMemo(() => {
      return appsList.map((app) => ({
        label: app.name,
        value: String(app.id),
        icon: app.icon,
        store: app.url.includes("apple")
          ? ("apple" as const)
          : ("google" as const),
      }));
    }, [appsList]);

    const handleSetAppInfo = useCallback(
      (id: string | null) => {
        if (!id) {
          onChange(null);
        }

        const appInfo = appsList.find((app) => String(app.id) === id);

        if (appInfo) {
          onChange(appInfo);
        }
      },
      [appsList, onChange],
    );

    const selectedValue: ComboboxItem | null = useMemo(() => {
      if (!value) {
        return null;
      }

      return {
        label: value.name,
        value: String(value.id),
      };
    }, [value]);

    return (
      <div ref={ref}>
        <AsyncSearchableSelect
          data={memoizedAppsList}
          value={selectedValue}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          width={width}
          onChange={handleSetAppInfo}
          loading={isFetching}
          placeholder={placeholder}
          error={error}
          disabled={disabled}
          containerProps={{
            disabled: disabled,
          }}
          clearable={clearable}
          onClear={() => onChange(null)}
          isSelectable={(item) => !item.label.includes("banned")}
          onInvalidSelect={() =>
            throwNewToastError("Application has been banned")
          }
          renderOption={(item) => (
            <Flex gap={8} align="center">
              {item?.icon ? (
                <img className={styles.item_icon} src={item.icon} alt="" />
              ) : null}
              {item.label}
              <ApplicationStoreIcon store={item.store} />
            </Flex>
          )}
        />
      </div>
    );
  },
);
